export const CAPTION_SIZE_MIN_PT = 4;
export const CAPTION_SIZE_MAX_PT = 40;
/**
 * The named sizes as points: the native caption styles
 * (subtitles/components/native/styles.ts: bodyShortBold, bodyShortBoldLarge,
 * heading5, heading4).
 */
export const CAPTION_SIZE_POINTS = {
    small: 14,
    medium: 16,
    large: 20,
    extraLarge: 28
};
/**
 * A size inside the range, or undefined when the value is not a number.
 *
 * @param {unknown} value - A size in points.
 * @returns {number|undefined}
 */
export function clampCaptionSizePt(value) {
    const points = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
    if (!Number.isFinite(points)) {
        return undefined;
    }
    return Math.min(CAPTION_SIZE_MAX_PT, Math.max(CAPTION_SIZE_MIN_PT, points));
}
/**
 * The named size nearest to a number of points. A tie goes to the larger size.
 *
 * @param {number} points - A size in points.
 * @returns {CaptionSizePreference}
 */
export function nearestCaptionSize(points) {
    let best = 'large';
    let bestDistance = Infinity;
    Object.keys(CAPTION_SIZE_POINTS).forEach(name => {
        const distance = Math.abs(CAPTION_SIZE_POINTS[name] - points);
        if (distance <= bestDistance) {
            best = name;
            bestDistance = distance;
        }
    });
    return best;
}
/**
 * The size the captions show at: the pinched number, else the named size.
 *
 * @param {Object} preferences - The caption preferences.
 * @returns {number}
 */
export function captionFontSizePt(preferences) {
    return clampCaptionSizePt(preferences.captionSizePt) ?? CAPTION_SIZE_POINTS[preferences.captionSize] ?? 20;
}
/**
 * The size during a pinch: it follows the distance between the two fingers,
 * from the size at the start of the pinch. Fingers apart make the text larger,
 * together make it smaller.
 *
 * @param {number} startPt - The size when the pinch started.
 * @param {number} startDistance - The distance between the fingers then.
 * @param {number} distance - The distance between the fingers now.
 * @returns {number}
 */
export function pinchCaptionSize(startPt, startDistance, distance) {
    if (!(startDistance > 0) || !(distance > 0)) {
        return clampCaptionSizePt(startPt) ?? CAPTION_SIZE_POINTS.large;
    }
    return clampCaptionSizePt(startPt * distance / startDistance) ?? CAPTION_SIZE_POINTS.large;
}
/**
 * The readout during a pinch: "24 pt".
 *
 * @param {number} points - A size in points.
 * @returns {string}
 */
export function captionSizeReadout(points) {
    return `${Math.round(points)} pt`;
}
/**
 * The size that a pinch saves: whole points.
 *
 * @param {number} points - The size at the end of the pinch.
 * @returns {number}
 */
export function savedCaptionSizePt(points) {
    return Math.round(clampCaptionSizePt(points) ?? CAPTION_SIZE_POINTS.large);
}
